// Merchant Category Code (MCC) list.
//
// Source: Payment Products / CardConnect canonical MCC list. Keep this
// list as the source of truth for the dropdown in the merchant intake
// form — operators were typing MCC numbers manually and getting them
// wrong, which CardConnect either rejects (boarding failure) or accepts
// silently against the wrong category (chargeback risk downstream).
//
// The form's MCC field renders this list via a searchable combobox
// that filters by code OR description so both an operator who knows
// the number and a merchant who only knows "I run a coffee shop" can
// find the right entry.
//
// Names are sorted alphabetically below for human searchability. The
// rendering layer can sort however it likes; the canonical shape is
// just (code, description) pairs.

export interface MccEntry {
  /** 4-digit MCC. Sent to CardConnect verbatim. */
  code: string;
  /** Human-readable description shown in the dropdown. */
  description: string;
}

export const MCC_LIST: readonly MccEntry[] = [
  { code: "7623", description: "A/C, Refrigeration Repair" },
  { code: "8931", description: "Accounting/Bookkeeping Services" },
  { code: "7311", description: "Advertising Services" },
  { code: "0763", description: "Agricultural Cooperative" },
  { code: "4511", description: "Airlines, Air Carriers" },
  { code: "4582", description: "Airports, Flying Fields" },
  { code: "4119", description: "Ambulance Services" },
  { code: "7996", description: "Amusement Parks/Carnivals" },
  { code: "5937", description: "Antique Reproductions" },
  { code: "5932", description: "Antique Shops" },
  { code: "7998", description: "Aquariums" },
  { code: "8911", description: "Architectural/Surveying Services" },
  { code: "5971", description: "Art Dealers and Galleries" },
  { code: "5970", description: "Artists Supply and Craft Shops" },
  { code: "7531", description: "Auto Body Repair Shops" },
  { code: "7535", description: "Auto Paint Shops" },
  { code: "7538", description: "Auto Service Shops" },
  { code: "5531", description: "Auto and Home Supply Stores" },
  { code: "6011", description: "Automated Cash Disburse" },
  { code: "5542", description: "Automated Fuel Dispensers" },
  { code: "8675", description: "Automobile Associations" },
  { code: "5533", description: "Automotive Parts and Accessories Stores" },
  { code: "5532", description: "Automotive Tire Stores" },
  { code: "9223", description: "Bail and Bond Payments" },
  { code: "5462", description: "Bakeries" },
  { code: "7929", description: "Bands, Orchestras" },
  { code: "7230", description: "Barber and Beauty Shops" },
  { code: "7995", description: "Betting/Casino Gambling" },
  { code: "5940", description: "Bicycle Shops" },
  { code: "7932", description: "Billiard/Pool Establishments" },
  { code: "5551", description: "Boat Dealers" },
  { code: "4457", description: "Boat Rentals and Leases" },
  { code: "5942", description: "Book Stores" },
  { code: "5192", description: "Books, Periodicals, and Newspapers" },
  { code: "7933", description: "Bowling Alleys" },
  { code: "4131", description: "Bus Lines" },
  { code: "8244", description: "Business/Secretarial Schools" },
  { code: "7399", description: "Business Services, Not Elsewhere Classified" },
  { code: "7278", description: "Buying/Shopping Services" },
  { code: "4899", description: "Cable, Satellite, and Other Pay TV/Radio" },
  { code: "5946", description: "Camera and Photographic Supply Stores" },
  { code: "5441", description: "Candy, Nut, and Confectionery Stores" },
  { code: "7512", description: "Car Rental Agencies" },
  { code: "7542", description: "Car Washes" },
  { code: "5511", description: "Car and Truck Dealers (New & Used)" },
  { code: "5521", description: "Car and Truck Dealers (Used Only)" },
  { code: "1750", description: "Carpentry Services" },
  { code: "7217", description: "Carpet/Upholstery Cleaning" },
  { code: "5811", description: "Caterers" },
  { code: "8398", description: "Charitable and Social Service Organizations" },
  { code: "5169", description: "Chemicals and Allied Products" },
  { code: "8351", description: "Child Care Services" },
  { code: "5641", description: "Children and Infants Wear Stores" },
  { code: "8049", description: "Chiropodists, Podiatrists" },
  { code: "8041", description: "Chiropractors" },
  { code: "5993", description: "Cigar Stores and Stands" },
  { code: "8641", description: "Civic, Social, Fraternal Associations" },
  { code: "7349", description: "Cleaning and Maintenance" },
  { code: "7296", description: "Clothing Rental" },
  { code: "8220", description: "Colleges, Universities" },
  { code: "5046", description: "Commercial Equipment" },
  { code: "5139", description: "Commercial Footwear" },
  { code: "7333", description: "Commercial Photography, Art and Graphics" },
  { code: "4111", description: "Commuter Transport, Ferries" },
  { code: "4816", description: "Computer Network Services" },
  { code: "7372", description: "Computer Programming" },
  { code: "7379", description: "Computer Repair" },
  { code: "5734", description: "Computer Software Stores" },
  { code: "5045", description: "Computers, Peripherals, and Software" },
  { code: "1771", description: "Concrete Work Services" },
  { code: "5039", description: "Construction Materials" },
  { code: "7392", description: "Consulting, Public Relations" },
  { code: "8241", description: "Correspondence Schools" },
  { code: "5977", description: "Cosmetic Stores" },
  { code: "7277", description: "Counseling Services" },
  { code: "7997", description: "Country Clubs" },
  { code: "4215", description: "Courier Services" },
  { code: "9211", description: "Court Costs, Including Alimony/Child Support" },
  { code: "7321", description: "Credit Reporting Agencies" },
  { code: "4411", description: "Cruise Lines" },
  { code: "5451", description: "Dairy Products Stores" },
  { code: "7911", description: "Dance Hall, Studios, Schools" },
  { code: "7273", description: "Dating/Escort Services" },
  { code: "8021", description: "Dentists, Orthodontists" },
  { code: "5311", description: "Department Stores" },
  { code: "7393", description: "Detective Agencies" },
  { code: "5815", description: "Digital Goods Media - Books, Movies, Music" },
  { code: "5817", description: "Digital Goods - Applications" },
  { code: "5816", description: "Digital Goods - Games" },
  { code: "5818", description: "Digital Goods - Large Merchant" },
  { code: "5964", description: "Direct Marketing - Catalog Merchant" },
  { code: "5965", description: "Direct Marketing - Catalog and Retail" },
  { code: "5967", description: "Direct Marketing - Inbound Telemarketing" },
  { code: "5960", description: "Direct Marketing - Insurance Services" },
  { code: "5969", description: "Direct Marketing - Other" },
  { code: "5966", description: "Direct Marketing - Outbound Telemarketing" },
  { code: "5968", description: "Direct Marketing - Subscription" },
  { code: "5962", description: "Direct Marketing - Travel" },
  { code: "5310", description: "Discount Stores" },
  { code: "8011", description: "Doctors" },
  { code: "5963", description: "Door-To-Door Sales" },
  { code: "5714", description: "Drapery, Window Covering, Upholstery Stores" },
  { code: "5813", description: "Drinking Places" },
  { code: "5912", description: "Drug Stores and Pharmacies" },
  { code: "5122", description: "Drugs, Drug Proprietaries, Druggist Sundries" },
  { code: "7216", description: "Dry Cleaners" },
  { code: "5099", description: "Durable Goods" },
  { code: "5309", description: "Duty Free Stores" },
  { code: "5812", description: "Eating Places, Restaurants" },
  { code: "8299", description: "Educational Services" },
  { code: "5997", description: "Electric Razor Stores" },
  { code: "5065", description: "Electrical Parts and Equipment" },
  { code: "1731", description: "Electrical Services" },
  { code: "7622", description: "Electronics Repair Shops" },
  { code: "5732", description: "Electronics Stores" },
  { code: "8211", description: "Elementary, Secondary Schools" },
  { code: "7361", description: "Employment/Temp Agencies" },
  { code: "7394", description: "Equipment Rental" },
  { code: "7342", description: "Exterminating Services" },
  { code: "5651", description: "Family Clothing Stores" },
  { code: "5814", description: "Fast Food Restaurants" },
  { code: "6012", description: "Financial Institutions" },
  { code: "9222", description: "Fines - Government Administrative Entities" },
  { code: "5723", description: "Firearm and Ammunition Stores (CA/CO/NY)" },
  { code: "5718", description: "Fireplace, Screens, and Accessories Stores" },
  { code: "5713", description: "Floor Covering Stores" },
  { code: "5992", description: "Florists" },
  { code: "5193", description: "Florists Supplies, Nursery Stock, Flowers" },
  { code: "5422", description: "Freezer and Locker Meat Provisioners" },
  { code: "5983", description: "Fuel Dealers (non-automotive)" },
  { code: "7261", description: "Funeral Services, Crematories" },
  { code: "7641", description: "Furniture Repair, Refinishing" },
  { code: "5712", description: "Furniture, Home Furnishings, Equipment" },
  { code: "5681", description: "Furriers and Fur Shops" },
  { code: "1520", description: "General Services" },
  { code: "5947", description: "Gift, Card, Novelty, and Souvenir Shops" },
  { code: "5231", description: "Glass, Paint, and Wallpaper Stores" },
  { code: "5950", description: "Glassware, Crystal Stores" },
  { code: "7992", description: "Golf Courses - Public" },
  { code: "7800", description: "Government-Owned Lotteries (US region)" },
  { code: "9406", description: "Government-Owned Lotteries (Non-US)" },
  { code: "7801", description: "Government Licensed Online Casinos" },
  { code: "9399", description: "Government Services" },
  { code: "5411", description: "Grocery Stores, Supermarkets" },
  { code: "5251", description: "Hardware Stores" },
  { code: "5072", description: "Hardware, Equipment, and Supplies" },
  { code: "7298", description: "Health and Beauty Spas" },
  { code: "5975", description: "Hearing Aids Sales and Supplies" },
  { code: "1711", description: "Heating, Plumbing, A/C" },
  { code: "5945", description: "Hobby, Toy, and Game Shops" },
  { code: "5200", description: "Home Supply Warehouse Stores" },
  { code: "8062", description: "Hospitals" },
  { code: "7011", description: "Hotels, Motels, and Resorts" },
  { code: "5722", description: "Household Appliance Stores" },
  { code: "5085", description: "Industrial Supplies" },
  { code: "7375", description: "Information Retrieval Services" },
  { code: "6399", description: "Insurance - Default" },
  { code: "6300", description: "Insurance Underwriting, Premiums" },
  { code: "9950", description: "Intra-Company Purchases" },
  { code: "5944", description: "Jewelry, Watches, Clocks, Silverware Stores" },
  { code: "0780", description: "Landscaping Services" },
  { code: "7211", description: "Laundries" },
  { code: "7210", description: "Laundry, Cleaning Services" },
  { code: "8111", description: "Legal Services, Attorneys" },
  { code: "5948", description: "Luggage and Leather Goods Stores" },
  { code: "5211", description: "Lumber, Building Materials Stores" },
  { code: "6010", description: "Manual Cash Disburse" },
  { code: "4468", description: "Marinas, Service and Supplies" },
  { code: "5262", description: "Marketplaces" },
  { code: "1740", description: "Masonry, Stonework, and Plaster" },
  { code: "7297", description: "Massage Parlors" },
  { code: "8099", description: "Medical Services" },
  { code: "8071", description: "Medical and Dental Labs" },
  { code: "5047", description: "Medical/Dental/Ophthalmic/Hospital Supplies" },
  { code: "8699", description: "Membership Organizations" },
  { code: "5611", description: "Men and Boys Clothing and Accessories" },
  { code: "5691", description: "Men, Women Clothing Stores" },
  { code: "5051", description: "Metal Service Centers" },
  { code: "5699", description: "Miscellaneous Apparel and Accessory Shops" },
  { code: "5599", description: "Miscellaneous Auto Dealers" },
  { code: "5499", description: "Miscellaneous Food Stores - Convenience" },
  { code: "5399", description: "Miscellaneous General Merchandise" },
  { code: "7299", description: "Miscellaneous General Services" },
  { code: "5719", description: "Miscellaneous Home Furnishing Stores" },
  { code: "2741", description: "Miscellaneous Publishing and Printing" },
  { code: "7999", description: "Miscellaneous Recreation Services" },
  { code: "7699", description: "Miscellaneous Repair Shops" },
  { code: "5999", description: "Miscellaneous Specialty Retail" },
  { code: "5271", description: "Mobile Home Dealers" },
  { code: "7832", description: "Motion Picture Theaters" },
  { code: "4214", description: "Motor Freight Carriers and Trucking" },
  { code: "5592", description: "Motor Homes Dealers" },
  { code: "5013", description: "Motor Vehicle Supplies and New Parts" },
  { code: "5571", description: "Motorcycle Shops and Dealers" },
  { code: "5561", description: "Motorcycle Shops, Dealers" },
  { code: "5733", description: "Music Stores - Instruments, Pianos, Sheet" },
  { code: "5994", description: "News Dealers and Newsstands" },
  { code: "6051", description: "Non-FI, Money Orders" },
  { code: "6540", description: "Non-FI, Stored Value Card Purchase/Load" },
  { code: "5199", description: "Nondurable Goods" },
  { code: "5261", description: "Nurseries, Lawn and Garden Supply Stores" },
  { code: "8050", description: "Nursing/Personal Care" },
  { code: "5021", description: "Office and Commercial Furniture" },
  { code: "8043", description: "Opticians, Eyeglasses" },
  { code: "8042", description: "Optometrists, Ophthalmologist" },
  { code: "5976", description: "Orthopedic Goods - Prosthetic Devices" },
  { code: "8031", description: "Osteopaths" },
  { code: "5921", description: "Package Stores - Beer, Wine, Liquor" },
  { code: "5198", description: "Paints, Varnishes, and Supplies" },
  { code: "7523", description: "Parking Lots, Garages" },
  { code: "4112", description: "Passenger Railways" },
  { code: "5933", description: "Pawn Shops" },
  { code: "5995", description: "Pet Shops, Pet Food, and Supplies" },
  { code: "5172", description: "Petroleum and Petroleum Products" },
  { code: "7395", description: "Photo Developing" },
  { code: "7221", description: "Photographic Studios" },
  { code: "5044", description: "Photographic, Photocopy, Microfilm Supplies" },
  { code: "7829", description: "Picture/Video Production" },
  { code: "5131", description: "Piece Goods, Notions, and Other Dry Goods" },
  { code: "5074", description: "Plumbing, Heating Equipment, Supplies" },
  { code: "8651", description: "Political Organizations" },
  { code: "9402", description: "Postal Services - Government Only" },
  { code: "5094", description: "Precious Stones, Metals, Watches, Jewelry" },
  { code: "8999", description: "Professional Services" },
  { code: "4225", description: "Public Warehousing and Storage" },
  { code: "7338", description: "Quick Copy, Repro, and Blueprint" },
  { code: "4011", description: "Railroads" },
  { code: "6513", description: "Real Estate Agents and Managers - Rentals" },
  { code: "5735", description: "Record Stores" },
  { code: "7519", description: "Recreational Vehicle Rentals" },
  { code: "5973", description: "Religious Goods Stores" },
  { code: "8661", description: "Religious Organizations" },
  { code: "1761", description: "Roofing/Siding, Sheet Metal" },
  { code: "7339", description: "Secretarial Support Services" },
  { code: "6211", description: "Security Brokers/Dealers" },
  { code: "5541", description: "Service Stations" },
  { code: "5949", description: "Sewing, Needlework, Fabric, Piece Goods" },
  { code: "7251", description: "Shoe Repair/Hat Cleaning" },
  { code: "5661", description: "Shoe Stores" },
  { code: "7629", description: "Small Appliance Repair" },
  { code: "5598", description: "Snowmobile Dealers" },
  { code: "1799", description: "Special Trade Services" },
  { code: "2842", description: "Specialty Cleaning" },
  { code: "5941", description: "Sporting Goods Stores" },
  { code: "7032", description: "Sporting/Recreation Camps" },
  { code: "7941", description: "Sports Clubs/Fields" },
  { code: "5655", description: "Sports and Riding Apparel Stores" },
  { code: "5972", description: "Stamp and Coin Stores" },
  { code: "5111", description: "Stationary, Office Supplies, Paper" },
  { code: "5943", description: "Stationery Stores, Office, School Supply" },
  { code: "5996", description: "Swimming Pools Sales" },
  { code: "4723", description: "TUI Travel - Germany" },
  { code: "5697", description: "Tailors, Alterations" },
  { code: "9311", description: "Tax Payments - Government Agencies" },
  { code: "7276", description: "Tax Preparation Services" },
  { code: "4121", description: "Taxicabs/Limousines" },
  { code: "4812", description: "Telecommunication Equipment, Telephone Sales" },
  { code: "4814", description: "Telecommunication Services" },
  { code: "4821", description: "Telegraph Services" },
  { code: "5998", description: "Tent and Awning Shops" },
  { code: "8734", description: "Testing Laboratories" },
  { code: "7922", description: "Theatrical Ticket Agencies" },
  { code: "7012", description: "Timeshares" },
  { code: "7534", description: "Tire Retreading and Repair" },
  { code: "4784", description: "Tolls/Bridge Fees" },
  { code: "7991", description: "Tourist Attractions and Exhibits" },
  { code: "7549", description: "Towing Services" },
  { code: "7033", description: "Trailer Parks, Campgrounds" },
  { code: "4789", description: "Transportation Services" },
  { code: "4722", description: "Travel Agencies, Tour Operators" },
  { code: "7511", description: "Truck Stops" },
  { code: "7513", description: "Truck/Utility Trailer Rentals" },
  { code: "2791", description: "Typesetting, Plate Making, and Related" },
  { code: "5978", description: "Typewriter Stores" },
  { code: "9405", description: "U.S. Federal Government Agencies" },
  { code: "5137", description: "Uniforms, Commercial Clothing" },
  { code: "5931", description: "Used Merchandise and Secondhand Stores" },
  { code: "4900", description: "Utilities" },
  { code: "5331", description: "Variety Stores" },
  { code: "0742", description: "Veterinary Services" },
  { code: "7993", description: "Video Amusement Game Supplies" },
  { code: "7994", description: "Video Game Arcades" },
  { code: "7841", description: "Video Tape Rental Stores" },
  { code: "8249", description: "Vocational/Trade Schools" },
  { code: "7631", description: "Watch/Jewelry Repair" },
  { code: "7692", description: "Welding Repair" },
  { code: "5300", description: "Wholesale Clubs" },
  { code: "5698", description: "Wig and Toupee Stores" },
  { code: "4829", description: "Wires, Money Orders" },
  { code: "5631", description: "Women Accessory and Specialty Shops" },
  { code: "5621", description: "Women Ready-To-Wear Stores" },
  { code: "5935", description: "Wrecking and Salvage Yards" },
];

/** O(1) lookup of an MCC entry by its 4-digit code. */
export const MCC_BY_CODE: Readonly<Record<string, MccEntry>> = Object.freeze(
  Object.fromEntries(MCC_LIST.map((m) => [m.code, m])),
);

function prefixMatch(haystack: string, q: string): boolean {
  const h = haystack.toLowerCase();
  if (h.startsWith(q)) return true;
  return h.split(/[^a-z0-9]+/).some((part) => part.startsWith(q));
}

/**
 * Prefix match on code or description. Empty query returns nothing
 * (combobox: type at least one character). Limit caps the panel.
 */
export function searchMcc(query: string, limit = 40): MccEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return MCC_LIST.filter(
    (m) => prefixMatch(m.code, q) || prefixMatch(m.description, q),
  ).slice(0, limit);
}

/** Format an entry for display: "5814 - Fast Food Restaurants". */
export function formatMccLabel(entry: MccEntry): string {
  return `${entry.code} - ${entry.description}`;
}
